import { useMemo, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Barcode,
  Minus,
  Plus,
  Search,
  ShoppingCart,
  PlusCircle,
  ReceiptText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  EmptyState,
  InlineNotice,
  Money,
  PageHeader,
  QuantityControl,
  StatusBadge,
} from "../components/ui";
import { db, type CachedProductUnit } from "../lib/db";
import {
  cartLineKey,
  unitPriceFor,
  useCart,
} from "../lib/cart";
import { todayBusinessDate } from "../lib/format";

export function SalesPage() {
  const navigate = useNavigate();
  const cart = useCart();
  const [query, setQuery] = useState("");
  const [selectedUnits, setSelectedUnits] = useState<
    Record<string, string>
  >({});
  const products = useLiveQuery(
    () => db.products.orderBy("name").toArray(),
    [],
    [],
  );

  const lastClosing = useLiveQuery(
    async () =>
      (await db.meta.get("last-closing"))
        ?.value as
        | {
            businessDate?: string;
            state?: "OPEN" | "CLOSED" | "REOPENED";
          }
        | null
        | undefined,
    [],
  );

  const closedToday =
    String(lastClosing?.businessDate ?? "").slice(0, 10) ===
      todayBusinessDate() &&
    lastClosing?.state !== "REOPENED";

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("vi");
    if (!needle) return products;

    return products.filter(
      (product) =>
        product.name
          .toLocaleLowerCase("vi")
          .includes(needle) ||
        product.sku
          .toLocaleLowerCase("vi")
          .includes(needle) ||
        product.barcode?.includes(needle) ||
        product.units.some(
          (unit) =>
            unit.name
              .toLocaleLowerCase("vi")
              .includes(needle) ||
            unit.barcode?.includes(needle),
        ),
    );
  }, [products, query]);

  const usedBaseByProduct = useMemo(() => {
    const result = new Map<string, number>();
    for (const line of cart.lines) {
      result.set(
        line.product.id,
        (result.get(line.product.id) ?? 0) +
          line.quantity * line.multiplier,
      );
    }
    return result;
  }, [cart.lines]);

  function startNewOrder() {
    if (cart.lines.length > 0) {
      const confirmed = window.confirm(
        "Đơn hiện tại đang có sản phẩm. Tạo đơn mới sẽ xóa giỏ hàng hiện tại. Bạn có muốn tiếp tục?",
      );
      if (!confirmed) return;
    }

    cart.clear();
    setQuery("");
    setSelectedUnits({});

    window.requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>(
        ".sales-page .search-box input",
      );
      input?.focus();
    });
  }

  function selectedUnitFor(
    productId: string,
    units: CachedProductUnit[],
  ): CachedProductUnit | null {
    const selected = selectedUnits[productId] ?? "base";
    if (selected === "base") return null;
    return units.find((unit) => unit.id === selected) ?? null;
  }

  if (closedToday) {
    return (
      <div className="page-stack sales-page">
        <PageHeader
          title="Bán hàng"
          description="Ngày kinh doanh hiện tại đã khóa giao dịch"
        />
        <InlineNotice
          tone="warning"
          title="Ngày hôm nay đã đóng sổ"
        >
          Không tạo thêm giao dịch sau khi đóng sổ. Nếu còn nghiệp vụ phát sinh,
          Chủ cửa hàng cần mở lại ngày trước.
        </InlineNotice>
        <button
          type="button"
          className="btn btn-secondary btn-lg"
          onClick={() => navigate("/closing")}
        >
          Xem trạng thái đóng sổ
        </button>
      </div>
    );
  }

  return (
    <div className="page-stack sales-page">
      <PageHeader
        title="Bán hàng"
        description="Tạo đơn mới, chọn sản phẩm và hoàn tất thanh toán"
      />

      <div className="sales-order-actions" aria-label="Thao tác đơn hàng">
        <button
          type="button"
          className="btn btn-primary btn-lg"
          onClick={startNewOrder}
        >
          <PlusCircle size={20} />
          Tạo đơn hàng
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-lg"
          onClick={() => navigate("/sales/history")}
        >
          <ReceiptText size={20} />
          Xem đơn hàng
        </button>
      </div>

      <div className="search-box">
        <Search size={20} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Tìm tên, SKU hoặc mã vạch"
          autoFocus
        />
        <button
          className="icon-button"
          type="button"
          title="Quét mã"
          aria-label="Quét mã vạch"
        >
          <Barcode size={22} />
        </button>
      </div>

      <div className="sales-layout">
        <section className="product-list">
          {filtered.length === 0 ? (
            <EmptyState
              title="Không tìm thấy sản phẩm"
              description="Thử từ khóa khác hoặc thêm sản phẩm trong Kho."
            />
          ) : (
            filtered.map((product) => {
              const unit = selectedUnitFor(
                product.id,
                product.units,
              );
              const key = cartLineKey(
                product.id,
                unit?.id,
              );
              const inCart = cart.lines.find(
                (line) => line.key === key,
              );
              const multiplier = unit?.multiplier ?? 1;
              const price = unitPriceFor(product, unit);
              const baseUsed =
                usedBaseByProduct.get(product.id) ?? 0;
              const canAdd =
                baseUsed + multiplier <=
                product.stockQty + 0.0005;
              const low =
                product.stockQty <= product.minStockQty;

              return (
                <Card
                  key={product.id}
                  className="product-row product-row-rich"
                >
                  <div className="product-avatar">
                    {product.name
                      .slice(0, 1)
                      .toUpperCase()}
                  </div>

                  <div className="product-main">
                    <strong>{product.name}</strong>

                    <div className="product-meta">
                      <Money value={price} />
                      <StatusBadge
                        tone={low ? "warning" : "neutral"}
                      >
                        Tồn {product.stockQty}{" "}
                        {product.baseUnit}
                      </StatusBadge>
                    </div>

                    {product.units.length > 0 ? (
                      <label className="inline-unit-select">
                        <span>Quy cách</span>
                        <select
                          value={
                            selectedUnits[product.id] ??
                            "base"
                          }
                          onChange={(event) =>
                            setSelectedUnits(
                              (current) => ({
                                ...current,
                                [product.id]:
                                  event.target.value,
                              }),
                            )
                          }
                        >
                          <option value="base">
                            1 {product.baseUnit}
                          </option>
                          {product.units.map(
                            (candidate) => (
                              <option
                                key={candidate.id}
                                value={candidate.id}
                              >
                                1 {candidate.name} ={" "}
                                {candidate.multiplier}{" "}
                                {product.baseUnit}
                              </option>
                            ),
                          )}
                        </select>
                      </label>
                    ) : null}

                    {product.trackExpiry &&
                    product.nextExpiryDate ? (
                      <small className="expiry-hint">
                        HSD gần nhất:{" "}
                        {new Intl.DateTimeFormat(
                          "vi-VN",
                          {
                            timeZone:
                              "Asia/Ho_Chi_Minh",
                          },
                        ).format(
                          new Date(
                            product.nextExpiryDate,
                          ),
                        )}
                      </small>
                    ) : null}
                  </div>

                  {inCart ? (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <QuantityControl
                        size="sm"
                        value={inCart.quantity}
                        min={0}
                        onChange={(qty) => cart.setQuantity(key, qty)}
                      />
                    </div>
                  ) : (
                    <button
                      className="btn btn-secondary add-product"
                      onClick={() =>
                        cart.add(product, unit)
                      }
                      disabled={
                        product.stockQty <= 0 ||
                        !canAdd
                      }
                    >
                      <Plus size={18} /> Thêm
                    </button>
                  )}
                </Card>
              );
            })
          )}
        </section>

        <aside className="cart-panel">
          <div className="section-title">
            <div>
              <ShoppingCart size={20} />
              <h2>Đơn hiện tại</h2>
            </div>
            <span>{cart.lines.length} dòng</span>
          </div>

          {cart.lines.length === 0 ? (
            <EmptyState
              title="Chưa có sản phẩm trong đơn"
              description="Chạm “Thêm” ở sản phẩm để bắt đầu."
            />
          ) : (
            <Card className="cart-card">
              <div className="cart-lines">
                {cart.lines.map((line) => (
                  <div
                    key={line.key}
                    className="cart-line"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "0.5rem",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {line.product.name}
                      </strong>
                      <small>
                        {line.unitPrice.toLocaleString(
                          "vi-VN",
                        )}{" "}
                        ₫ / {line.unit?.name ?? line.product.baseUnit}
                      </small>
                    </div>

                    <QuantityControl
                      size="sm"
                      value={line.quantity}
                      min={0}
                      onChange={(qty) => cart.setQuantity(line.key, qty)}
                    />

                    <Money
                      value={
                        line.unitPrice *
                        line.quantity
                      }
                    />
                  </div>
                ))}
              </div>

              <div className="cart-total">
                <span>Tổng cộng</span>
                <Money
                  value={cart.total}
                  className="money-large"
                />
              </div>

              <button
                className="btn btn-primary btn-lg"
                onClick={() =>
                  navigate("/sales/payment")
                }
              >
                Thanh toán
              </button>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}
